// frontend/src/pages/Agreements/AgreementList.jsx
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { getAgreements } from "../../api/agreement.api.js";
import { getEntities } from "../../api/entity.api.js";
import { getVendors } from "../../api/vendor.api.js";
import { getPartners } from "../../api/partner.api.js";
import {
  PageHeader,
  Spinner,
  EmptyState,
  StatusBadge,
  formatDate,
} from "../../components/ui";
import { Plus, ChevronRight, X } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "DRAFT", label: "Draft" },
  { value: "REVISED", label: "Revised" },
  { value: "UNDER_REVIEW", label: "Under Review" },
  { value: "REVISION_REQUESTED", label: "Needs Revision" },
  { value: "APPROVED", label: "Approved" },
  { value: "EXPIRED", label: "Expired" },
  { value: "TERMINATED", label: "Terminated" },
];

function FilterTag({ label, onRemove }) {
  return (
    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 border border-brand-200 text-xs text-brand-700">
      {label}
      <button onClick={onRemove} className="hover:text-brand-900 ml-0.5">
        <X size={11} />
      </button>
    </span>
  );
}

function MultiSelect({
  label,
  options,
  selected,
  onChange,
  getLabel,
  getValue,
}) {
  const count = selected.length;
  return (
    <div className="flex flex-col gap-1 min-w-[160px]">
      <label className="text-xs text-gray-500 font-medium">
        {label}
        {count > 0 && <span className="ml-1 text-brand-600">({count})</span>}
      </label>
      <div className="relative">
        <details className="group">
          <summary className="flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer hover:bg-gray-50 list-none text-sm text-gray-700">
            <span>{count === 0 ? `All ${label}s` : `${count} selected`}</span>
            <svg
              className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </summary>
          <div className="absolute z-20 mt-1 w-full min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {options.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-400 italic">
                No options available
              </p>
            ) : (
              options.map((opt) => {
                const val = getValue ? getValue(opt) : opt.id;
                const lbl = getLabel ? getLabel(opt) : opt.name;
                const checked = selected.includes(val);
                return (
                  <label
                    key={val}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        onChange(
                          checked
                            ? selected.filter((v) => v !== val)
                            : [...selected, val],
                        )
                      }
                      className="rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-700">{lbl}</span>
                  </label>
                );
              })
            )}
          </div>
        </details>
      </div>
    </div>
  );
}

export default function AgreementList() {
  const { isAdmin, isMaster, isVendor, isPartner } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Filter state — initialised from URL params ────────────────────────────
  // Advanced filters use arrays for multi-select
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [status, setStatus] = useState(searchParams.get("status") ?? "");
  const [groupIds, setGroupIds] = useState(() =>
    searchParams.getAll("groupId"),
  );
  const [entityIds, setEntityIds] = useState(() =>
    searchParams.getAll("entityId"),
  );
  const [vendorIds, setVendorIds] = useState(() =>
    searchParams.getAll("vendorId"),
  );
  const [partnerIds, setPartnerIds] = useState(() =>
    searchParams.getAll("partnerId"),
  );
  const [page, setPage] = useState(1);

  const canSubmit = isVendor || isPartner || isMaster;

  // ── Sync state → URL params (multi-value supported) ─────────────────────
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    groupIds.forEach((id) => params.append("groupId", id));
    entityIds.forEach((id) => params.append("entityId", id));
    vendorIds.forEach((id) => params.append("vendorId", id));
    partnerIds.forEach((id) => params.append("partnerId", id));
    setSearchParams(params, { replace: true });
    setPage(1);
  }, [search, status, groupIds, entityIds, vendorIds, partnerIds]);

  // ── Agreements query ──────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: [
      "agreements",
      { search, status, groupIds, entityIds, vendorIds, partnerIds, page },
    ],
    queryFn: () =>
      getAgreements({
        ...(search && { search }),
        ...(status && { status }),
        ...(groupIds.length && { groupId: groupIds }),
        ...(entityIds.length && { entityId: entityIds }),
        ...(vendorIds.length && { vendorId: vendorIds }),
        ...(partnerIds.length && { partnerId: partnerIds }),
        page,
        limit: 20,
      }),
    keepPreviousData: true,
  });

  // ── Filter option queries (admin only) ────────────────────────────────────
  const { data: entities } = useQuery({
    queryKey: ["entities-for-filter"],
    queryFn: () => getEntities(),
    enabled: isAdmin,
  });

  const { data: vendors } = useQuery({
    queryKey: ["vendors-for-filter"],
    queryFn: () => getVendors({ limit: 200 }),
    enabled: isMaster,
    select: (res) => res?.data ?? res ?? [],
  });

  const { data: partners } = useQuery({
    queryKey: ["partners-for-filter"],
    queryFn: () => getPartners({ limit: 200 }),
    enabled: isMaster,
    select: (res) => res?.data ?? res ?? [],
  });

  // Derive unique groups from entities
  const groups = entities
    ? [
        ...new Map(
          entities.filter((e) => e.group).map((e) => [e.group.id, e.group]),
        ).values(),
      ].sort((a, b) => a.name.localeCompare(b.name))
    : [];

  // Entity list cascades — if groups selected, only show entities in those groups
  const entityOptions = groupIds.length
    ? entities?.filter((e) => groupIds.includes(e.group?.id))
    : entities;

  const agreements = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  const activeFilterCount =
    groupIds.length + entityIds.length + vendorIds.length + partnerIds.length;
  const hasActiveFilters = !!(search || status || activeFilterCount);

  function clearFilters() {
    setSearch("");
    setStatus("");
    setGroupIds([]);
    setEntityIds([]);
    setVendorIds([]);
    setPartnerIds([]);
  }

  // Toggle a value in/out of a multi-select array
  function toggle(arr, setArr, value) {
    setArr(
      arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
    );
  }

  function handleGroupToggle(gid) {
    const next = groupIds.includes(gid)
      ? groupIds.filter((v) => v !== gid)
      : [...groupIds, gid];
    setGroupIds(next);
    // Remove entities that no longer belong to any selected group
    if (next.length > 0 && entityIds.length > 0) {
      setEntityIds(
        entityIds.filter((eid) =>
          entities?.find((e) => e.id === eid && next.includes(e.group?.id)),
        ),
      );
    }
  }

  return (
    <div>
      <PageHeader
        title="Agreements"
        subtitle={total ? `${total} total` : ""}
        action={
          canSubmit && (
            <Link
              to="/agreements/new"
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <Plus size={16} /> New Agreement
            </Link>
          )
        }
      />

      {/* ── Filter bar — always visible ───────────────────────────────── */}
      <div className="mb-5 space-y-3">
        {/* Row 1: search + status */}
        <div className="flex flex-wrap gap-3">
          <input
            className="input max-w-xs text-sm"
            placeholder="Search by title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="input w-44 text-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600"
            >
              <X size={14} /> Clear all
            </button>
          )}
        </div>

        {/* Row 2: multi-select dropdowns — admin only, always shown */}
        {isAdmin && (
          <div className="flex flex-wrap gap-3 items-start">
            <MultiSelect
              label="Group"
              options={groups}
              selected={groupIds}
              onChange={(next) => {
                setGroupIds(next);
                if (next.length > 0 && entityIds.length > 0) {
                  setEntityIds(
                    entityIds.filter((eid) =>
                      entities?.find(
                        (e) => e.id === eid && next.includes(e.group?.id),
                      ),
                    ),
                  );
                }
              }}
            />
            <MultiSelect
              label="Entity"
              options={entityOptions ?? []}
              selected={entityIds}
              onChange={setEntityIds}
            />
            {isMaster && (
              <MultiSelect
                label="Vendor"
                options={vendors ?? []}
                selected={vendorIds}
                onChange={setVendorIds}
                getLabel={(v) => v.companyName}
              />
            )}
            {isMaster && (
              <MultiSelect
                label="Partner"
                options={partners ?? []}
                selected={partnerIds}
                onChange={setPartnerIds}
                getLabel={(p) => p.companyName}
              />
            )}
          </div>
        )}

        {/* Row 3: active filter tags */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2">
            {groupIds.map((gid) => (
              <FilterTag
                key={gid}
                label={`Group: ${groups.find((g) => g.id === gid)?.name ?? gid}`}
                onRemove={() => {
                  const next = groupIds.filter((v) => v !== gid);
                  setGroupIds(next);
                  if (next.length === 0) setEntityIds([]);
                  else
                    setEntityIds(
                      entityIds.filter((eid) =>
                        entities?.find(
                          (e) => e.id === eid && next.includes(e.group?.id),
                        ),
                      ),
                    );
                }}
              />
            ))}
            {entityIds.map((eid) => (
              <FilterTag
                key={eid}
                label={`Entity: ${entities?.find((e) => e.id === eid)?.name ?? eid}`}
                onRemove={() =>
                  setEntityIds(entityIds.filter((v) => v !== eid))
                }
              />
            ))}
            {vendorIds.map((vid) => (
              <FilterTag
                key={vid}
                label={`Vendor: ${vendors?.find((v) => v.id === vid)?.companyName ?? vid}`}
                onRemove={() =>
                  setVendorIds(vendorIds.filter((v) => v !== vid))
                }
              />
            ))}
            {partnerIds.map((pid) => (
              <FilterTag
                key={pid}
                label={`Partner: ${partners?.find((p) => p.id === pid)?.companyName ?? pid}`}
                onRemove={() =>
                  setPartnerIds(partnerIds.filter((v) => v !== pid))
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Agreement list ─────────────────────────────────────────────── */}
      {isLoading ? (
        <Spinner />
      ) : !agreements.length ? (
        <EmptyState
          title="No agreements found"
          description={
            canSubmit
              ? "Submit your first agreement to get started."
              : undefined
          }
          action={
            canSubmit && (
              <Link to="/agreements/new" className="btn-primary text-sm">
                Submit Agreement
              </Link>
            )
          }
        />
      ) : (
        <>
          <div className="card divide-y divide-gray-100">
            {agreements.map((a) => {
              const counterparty =
                a.vendor?.companyName ?? a.partner?.companyName;
              const daysLeft = Math.ceil(
                (new Date(a.endDate) - Date.now()) / (1000 * 60 * 60 * 24),
              );
              const expiringSoon =
                a.status === "APPROVED" && daysLeft >= 0 && daysLeft <= 30;

              return (
                <Link
                  key={a.id}
                  to={`/agreements/${a.id}`}
                  className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {a.title}
                      </p>
                      {expiringSoon && (
                        <span className="badge bg-amber-100 text-amber-700 flex-shrink-0">
                          {daysLeft}d left
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {isAdmin && counterparty && (
                        <span className="mr-1">{counterparty} ·</span>
                      )}
                      {a.entityCache?.name ?? (
                        <span className="italic text-gray-400">
                          No entity assigned
                        </span>
                      )}
                      {a.startDate && a.endDate && (
                        <span>
                          {" "}
                          · {formatDate(a.startDate)} – {formatDate(a.endDate)}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                    <StatusBadge status={a.status} />
                    <ChevronRight size={15} className="text-gray-400" />
                  </div>
                </Link>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages} · {total} results
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="btn-secondary text-sm py-1.5 px-3 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="btn-secondary text-sm py-1.5 px-3 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
